import React, {useMemo, useState, useRef, useEffect} from 'react';
import {Platform, Text, View, TouchableOpacity} from 'react-native';
import TextTooltip from '../../../../shared/components/ui/TextTooltip/TextTooltip';
import {useTranslation} from 'react-i18next';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {createPaginationStyles} from '../../../../shared/components/ui/Pagination/Pagination.styles';
import ListPaginationLeftDoubleArrowIcon from '../../../../shared/assets/icons/ListPaginationLeftDoubleArrowIcon';
import ListPaginationLeftSingleArrowIcon from '../../../../shared/assets/icons/ListPaginationLeftSingleArrowIcon';
import ListPaginationRightSingleArrowIcon from '../../../../shared/assets/icons/ListPaginationRightSingleArrowIcon';
import ListPaginationRightDoubleArrowIcon from '../../../../shared/assets/icons/ListPaginationRightDoubleArrowIcon';
import Dropdown from '../../../../shared/components/ui/Dropdown/Dropdown';
import {PaginationProps} from '../../../../shared/components/ui/Pagination/PaginationProps';
import {useShiftStatusGuard} from '@shared/hooks/useShiftStatusGuard';

const Pagination = ({
  currentPage,
  totalItems,
  pageSize,
  pageSizeOptions = [9, 18, 27],
  maxVisiblePages = 5,
  onPageChange,
  onPageSizeChange,
  inputRef,
  customContainerStyles,
  skipShiftGuard = false,
}: PaginationProps) => {
  const {t} = useTranslation();
  const theme = useScreenTheme('pagination');
  const styles = useMemo(() => createPaginationStyles(theme), [theme]);

  const totalPages = Math.ceil(totalItems / pageSize);

  const finalPageSizeOptions = useMemo(() => {
    const options = pageSizeOptions.filter(size => size < totalItems);
    const nextBiggerOption = pageSizeOptions.find(size => size >= totalItems);
    if (nextBiggerOption) {
      options.push(nextBiggerOption);
    }
    if (options.length > 0) {
      return options;
    }
    if (pageSizeOptions.every(size => size < totalItems)) {
      return pageSizeOptions;
    }
    return [pageSizeOptions[0]];
  }, [totalItems, pageSizeOptions]);

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const summaryText = `${t(
    'dashboard.workOrders.grids.showing',
  )} ${startItem} ${t('dashboard.workOrders.grids.to')} ${endItem} ${t(
    'dashboard.workOrders.grids.of',
  )} ${totalItems} ${t('dashboard.workOrders.grids.entries')}`;

  /** Ref to the visible summary Text element.  On web we read scrollHeight vs
   *  clientHeight after each render to detect whether the numberOfLines=1 clip
   *  is actually hiding any content. */
  const summaryTextRef = useRef<any>(null);
  const [isSummaryTruncated, setIsSummaryTruncated] = useState(false);
  const {checkShiftStatus} = useShiftStatusGuard();

  useEffect(() => {
    // Only meaningful on web — on native the DOM overflow APIs are absent.
    if (Platform.OS !== 'web') return;

    const check = () => {
      const el = summaryTextRef.current;
      if (!el) return;
      // react-native-web renders numberOfLines={1} as white-space:nowrap +
      // overflow:hidden, so horizontal scrollWidth > clientWidth detects the
      // truncation.  Fall back to vertical scrollHeight check for browsers
      // that use -webkit-line-clamp instead.
      setIsSummaryTruncated(
        el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight,
      );
    };

    const timer = setTimeout(check, 0);
    // Re-evaluate whenever the window (and therefore the container) is resized.
    window.addEventListener('resize', check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', check);
    };
  }, [summaryText]);

  const handlePageSizeSelect = async (option: {
    label: string | number;
    value: string | number;
  }) => {
    if (!skipShiftGuard) {
      const ok = await checkShiftStatus();
      if (!ok) return;
    }
    onPageSizeChange(Number(option.value));
  };

  const renderPageNumbers = () => {
    if (totalPages <= 1) return null;

    const pageNumbers: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pageNumbers.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(
          1,
          '...',
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pageNumbers.push(
          1,
          '...',
          currentPage - 1,
          currentPage,
          currentPage + 1,
          '...',
          totalPages,
        );
      }
    }

    return pageNumbers.map((page, index) => {
      if (page === '...') {
        return (
          <View
            key={`ellipsis-${index}`}
            style={styles.paginationDots}
            accessible={false}
            importantForAccessibility="no-hide-descendants">
            <Text
              style={styles.ellipsis}
              accessible={false}
              importantForAccessibility="no">
              ...
            </Text>
          </View>
        );
      }

      const isActive = page === currentPage;

      return (
        <TouchableOpacity
          key={`page-${page}`}
          onPress={async () => {
            if (!skipShiftGuard) {
              const ok = await checkShiftStatus();
              if (!ok) return;
            }
            onPageChange(page as number);
          }}
          style={[
            styles.pageButton,
            page === currentPage && styles.activePageButton,
          ]}
          testID={`pagination-page-${page}`}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={
            isActive
              ? t('common.pagination.accessibility.pageButtonSelected', {page})
              : t('common.pagination.accessibility.pageButtonNotSelected', {
                  page,
                })
          }
          accessibilityState={{selected: isActive}}>
          <Text
            style={
              page === currentPage ? styles.activePageText : styles.pageText
            }
            accessible={false}
            importantForAccessibility="no">
            {page}
          </Text>
        </TouchableOpacity>
      );
    });
  };

  return (
    <View style={[styles.contentWrapper, customContainerStyles]} testID="pagination-container">
      {/* Left Section */}
      <View style={styles.leftSection}>
        <Text
          style={styles.text}
          accessible={false}
          importantForAccessibility="no">
          {t('dashboard.workOrders.grids.rowsPerPage')}
        </Text>
        <Dropdown
          label=""
          selectedSort={{label: `${pageSize}`, value: pageSize}}
          options={finalPageSizeOptions.map(size => ({
            label: `${size}`,
            value: size,
          }))}
          onSelect={handlePageSizeSelect}
          customTextStyles={styles.customTextStyles}
          customContainerStyles={styles.customContainerStyles}
          inputRef={inputRef}
          testID="pagination-rows-dropdown"
          accessibilityLabel={t(
            'common.pagination.accessibility.rowsPerPageLabel',
            {size: pageSize},
          )}
          accessibilityHint={t(
            'common.pagination.accessibility.rowsPerPageHint',
          )}
          accessibilityRole="combobox"
        />
        <View
          style={styles.textRow}
          accessible={true}
          accessibilityLabel={summaryText}
          // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
          tabIndex={0}
          focusable={true}>
          {/* Hidden text nodes retained for test compatibility.
              On web: aria-hidden ensures WCAG automated tools skip these nodes
              (WCAG 1.4.3 – prevents false contrast failure on opacity:0 elements). */}
          <Text
            testID="pagination-summary-showing"
            style={styles.hiddenTestNode}
            accessible={false}
            aria-hidden={Platform.OS === 'web' ? true : undefined}>
            {`${t('dashboard.workOrders.grids.showing')}`}
          </Text>
          <Text
            testID="pagination-summary-range"
            style={styles.hiddenTestNode}
            accessible={false}
            aria-hidden={Platform.OS === 'web' ? true : undefined}>
            {`${startItem} ${t('dashboard.workOrders.grids.to')} ${endItem}`}
          </Text>
          <Text
            testID="pagination-summary-total"
            style={styles.hiddenTestNode}
            accessible={false}
            aria-hidden={Platform.OS === 'web' ? true : undefined}>
            {` ${t('dashboard.workOrders.grids.of')} ${totalItems} ${t(
              'dashboard.workOrders.grids.entries',
            )}`}
          </Text>
          <TextTooltip
            tooltipText={summaryText}
            placement="above"
            webFocusable={false}
            disabled={!isSummaryTruncated}>
            {/* Visible 1-line text with mixed bold/regular styles.
                ref is used on web to check scrollHeight > clientHeight. */}
            <Text
              ref={summaryTextRef}
              numberOfLines={1}
              ellipsizeMode="tail"
              style={styles.summaryTextOuter}
              accessible={false}>
              <Text style={styles.text}>
                {t('dashboard.workOrders.grids.showing')}{' '}
              </Text>
              <Text style={styles.darkText}>
                {startItem} {t('dashboard.workOrders.grids.to')} {endItem}
              </Text>
              <Text style={styles.text}>
                {' '}
                {t('dashboard.workOrders.grids.of')} {totalItems}{' '}
                {t('dashboard.workOrders.grids.entries')}
              </Text>
            </Text>
          </TextTooltip>
        </View>
      </View>

      {/* Right Section */}
      {totalPages > 1 && (
        <View style={styles.rightSection} accessibilityRole="toolbar">
          <TouchableOpacity
            onPress={async () => {
              if (!skipShiftGuard) {
                const ok = await checkShiftStatus();
                if (!ok) return;
              }
              if (currentPage !== 1) {
                onPageChange(currentPage - 1);
              }
            }}
            testID="pagination-prev-button"
            accessible={true}
            accessibilityLabel={
              currentPage === 1
                ? t('common.pagination.accessibility.goToPreviousPage')
                : t('common.pagination.accessibility.prevPageLabel', {
                    page: currentPage,
                    total: totalPages,
                  })
            }
            accessibilityHint={
              currentPage === 1
                ? undefined
                : t('common.pagination.accessibility.prevPageHint')
            }
            accessibilityRole="button"
            accessibilityState={{disabled: currentPage === 1}}
            // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
            tabIndex={0}
            focusable={true}
            style={styles.paginationButtons}>
            {/* <ListPaginationLeftDoubleArrowIcon
              borderColor={(theme as any).listPaginationBorder}
              color={
                currentPage === 1
                  ? (theme as any).listPaginationDisable
                  : (theme as any).listPaginationEnable
              }
            /> */}
            <Text
              style={styles.paginationButtonsText}
              importantForAccessibility="no"
              accessible={false}>
              {t('common.pagination.prev')}
            </Text>
          </TouchableOpacity>
          {/* <TouchableOpacity
            disabled={currentPage === 1}
            onPress={() => onPageChange(currentPage - 1)}
            testID="pagination-prev-button"
            accessibilityLabel="Go to previous page"
            accessibilityRole="button"
            accessibilityState={{ disabled: currentPage === 1 }}
            style={styles.paginationButtons}
          >
            <ListPaginationLeftSingleArrowIcon
              borderColor={(theme as any).listPaginationBorder}
              color={
                currentPage === 1
                  ? (theme as any).listPaginationDisable
                  : (theme as any).listPaginationEnable
              }
            />
          </TouchableOpacity> */}

          {renderPageNumbers()}

          {/* <TouchableOpacity
            disabled={currentPage === totalPages || totalPages === 0}
            onPress={() => onPageChange(currentPage + 1)}
            testID="pagination-next-button"
            accessibilityLabel="Go to next page"
            accessibilityRole="button"
            accessibilityState={{ disabled: currentPage === totalPages }}
          >
            <ListPaginationRightSingleArrowIcon
              borderColor={(theme as any).listPaginationBorder}
              color={
                currentPage === totalPages || totalPages === 0
                  ? (theme as any).listPaginationDisable
                  : (theme as any).listPaginationEnable
              }
            />
          </TouchableOpacity> */}
          <TouchableOpacity
            onPress={async () => {
              if (!skipShiftGuard) {
                const ok = await checkShiftStatus();
                if (!ok) return;
              }
              if (currentPage !== totalPages && totalPages !== 0) {
                onPageChange(currentPage + 1);
              }
            }}
            testID="pagination-next-button"
            accessible={true}
            accessibilityLabel={
              currentPage === totalPages || totalPages === 0
                ? t('common.pagination.accessibility.goToNextPage')
                : t('common.pagination.accessibility.nextPageLabel', {
                    page: currentPage,
                    total: totalPages,
                  })
            }
            accessibilityHint={
              currentPage === totalPages || totalPages === 0
                ? undefined
                : t('common.pagination.accessibility.nextPageHint')
            }
            accessibilityRole="button"
            accessibilityState={{
              disabled: currentPage === totalPages || totalPages === 0,
            }}
            // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
            tabIndex={0}
            focusable={true}
            style={styles.paginationButtons}>
            {/* <ListPaginationRightDoubleArrowIcon
              borderColor={(theme as any).listPaginationBorder}
              color={
                currentPage === totalPages || totalPages === 0
                  ? (theme as any).listPaginationDisable
                  : (theme as any).listPaginationEnable
              }
            /> */}
            <Text
              style={styles.paginationButtonsText}
              importantForAccessibility="no"
              accessible={false}>
              {t('common.pagination.next')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

export default Pagination;
